/**
 * Agent dispatch + model preset schemas — mirror chaos.model_presets and
 * the chaos-agent-runtime dispatch endpoint contract.
 *
 * Phase 3 brings the streaming wire shape (token / done / error / status)
 * the chat tab consumes via the `/ws/cards/:cardId/io` upgrade.
 */
import { z } from 'zod';

// LLM providers we route to via chaos-model-routing. The list is
// open-set (managed in chaos.model_presets); these are the canonical IDs
// used in URL paths and JSON.
export const providerKeys = ['anthropic', 'openai', 'google', 'xai'] as const;
export const providerKeySchema = z.enum(providerKeys);
export type ProviderKey = z.infer<typeof providerKeySchema>;

// Thinking depth options (Anthropic-style); the gateway translates these
// into provider-specific reasoning parameters.
// Aligned with MODEL_SELECTION_AND_AUTO_SWITCHING.md §2: off | standard | deep.
export const thinkingDepths = ['off', 'standard', 'deep'] as const;
export const thinkingDepthSchema = z.enum(thinkingDepths);
export type ThinkingDepth = z.infer<typeof thinkingDepthSchema>;

// "Effort tier" — fast / balanced / deep — drives provider+model selection.
// Aligned with MODEL_SELECTION_AND_AUTO_SWITCHING.md §2.
export const effortTiers = ['fast', 'balanced', 'deep'] as const;
export const effortTierSchema = z.enum(effortTiers);
export type EffortTier = z.infer<typeof effortTierSchema>;

// The serialised ModelChoice tuple stored in chaos.model_presets.model_choice_jsonb.
// Mirrors the Python ModelChoice contract (services/model-routing/.../schemas/model_choice.py).
export const modelChoiceSchema = z.object({
  provider: providerKeySchema,
  model: z.string().min(1).max(120),
  tier: effortTierSchema,
  thinking: thinkingDepthSchema,
  researchMode: z.boolean().default(false),
  temperature: z.number().min(0).max(2).nullable().default(null),
  maxTokens: z.number().int().positive().nullable().default(null),
  toolUse: z.boolean().default(true),
  vision: z.boolean().default(false),
});
export type ModelChoice = z.infer<typeof modelChoiceSchema>;

// chaos.model_presets row — the wire shape returned by /api/routing/presets.
export const modelPresetSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  description: z.string().nullable(),
  modelChoice: modelChoiceSchema,
  isDefault: z.boolean(),
  isSystem: z.boolean(),
  createdAt: z.string().datetime(),
  unavailable: z.boolean().optional(),
  unavailableReason: z.string().optional(),
});
export type ModelPreset = z.infer<typeof modelPresetSchema>;

// PUT /api/routing/preset/:cardId — persist the card's active preset.
export const setCardPresetRequestSchema = z.object({
  presetId: z.string().uuid().nullable(),
});
export type SetCardPresetRequest = z.infer<typeof setCardPresetRequestSchema>;

// Optional per-card override mirrors the active ModelChoice — when set,
// the column-driven auto-switch is bypassed.
export const cardPresetStateSchema = z.object({
  cardId: z.string().uuid(),
  presetId: z.string().uuid().nullable(),
  effective: modelChoiceSchema,
  // Source of `effective`: did it come from the explicit `presetId`, the
  // column default, or the workspace fallback? Useful for the picker UI.
  source: z.enum(['card', 'column', 'board', 'project', 'workspace', 'system']),
});
export type CardPresetState = z.infer<typeof cardPresetStateSchema>;

// Payload posted to /api/agent/dispatch (gateway → chaos-agent-runtime).
export const agentDispatchRequestSchema = z.object({
  cardId: z.string().uuid(),
  // Optional per-dispatch override — falls back to card's preset, then
  // column default, then workspace default.
  modelPresetId: z.string().uuid().optional(),
  // Message to append to the card chat (markdown, max 64 KB).
  message: z.string().min(1).max(65_536),
  // Dispatch mode — see CONTROL_SURFACE_LAYOUT.md §7. `run` is the default
  // path; `force` skips the Constitution check (founder-only); `dryRun`
  // returns the resolved choice + system prompt without invoking the LLM.
  mode: z.enum(['run', 'force', 'dryRun']).default('run'),
  // Optional structured tool hints (Phase 3 will formalise).
  toolHints: z.array(z.string().min(1).max(120)).optional(),
});
export type AgentDispatchRequest = z.infer<typeof agentDispatchRequestSchema>;

export const agentDispatchAckSchema = z.object({
  cardId: z.string().uuid(),
  // The session that handled the dispatch (Phase 3 wires into the
  // session-runtime sleep/wake state machine).
  sessionId: z.string().uuid(),
  // WebSocket channel the client should subscribe to for streaming.
  streamUrl: z.string().min(1),
  // Dispatch identifier — frames carry it so concurrent dispatches can be
  // demultiplexed by the consumer.
  dispatchId: z.string().uuid(),
  acceptedAt: z.string().datetime(),
});
export type AgentDispatchAck = z.infer<typeof agentDispatchAckSchema>;

// Wire frames forwarded over /ws/cards/:cardId/io.
//
// `token` — incremental LLM output (the chat composes them into a single
//           assistant message; the UI auto-scrolls).
// `tool_call` — agent invoked a tool; rendered as an inline pill.
// `tool_result` — tool returned; folded into the same pill.
// `status` — session state machine transition (see SESSION_RUNTIME §3.1).
// `done`  — terminal frame for a dispatch; payload carries token + cost.
// `error` — terminal frame on failure; friendly message + opaque code.
// `ping`  — keep-alive; ignored by the consumer.
export const cardStreamFrameSchema = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('hello'),
    cardId: z.string().uuid(),
  }),
  z.object({ event: z.literal('ping') }),
  z.object({
    event: z.literal('token'),
    dispatchId: z.string().uuid().optional(),
    text: z.string(),
  }),
  z.object({
    event: z.literal('tool_call'),
    dispatchId: z.string().uuid().optional(),
    name: z.string(),
    input: z.unknown().optional(),
  }),
  z.object({
    event: z.literal('tool_result'),
    dispatchId: z.string().uuid().optional(),
    name: z.string(),
    output: z.unknown().optional(),
  }),
  z.object({
    event: z.literal('status'),
    state: z.enum(['created', 'active', 'idle', 'sleeping', 'waking', 'dead']),
    note: z.string().optional(),
  }),
  z.object({
    event: z.literal('system'),
    text: z.string(),
  }),
  z.object({
    event: z.literal('done'),
    dispatchId: z.string().uuid().optional(),
    tokensInput: z.number().int().nonnegative().optional(),
    tokensOutput: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
  }),
  z.object({
    event: z.literal('error'),
    dispatchId: z.string().uuid().optional(),
    code: z.string(),
    message: z.string(),
  }),
]);
export type CardStreamFrame = z.infer<typeof cardStreamFrameSchema>;
